/**
 * Outside-click stack — a single document-level mousedown listener shared by
 * every layered flow popover/dropdown (`ResponsivePopover` via
 * `useClickOutside`, and the nested pickers — `FieldPicker`,
 * `PersonValuePicker`, `PersonMultiValuePicker`, `MultiValueInput` — via
 * `useDropdownAnchor`) so that when one is nested inside another, a single
 * outside click closes only the TOPMOST layer instead of every registered
 * layer evaluating (and possibly closing) independently. Mirrors
 * `ui/escape-stack.ts`'s exact shape and StrictMode reasoning — same bug
 * family, one keyed by `keydown`/Escape, this one by `mousedown`.
 *
 * ── The bug this exists to fix (Spencer report, 2026-08-13) ───────────────
 * `ResponsivePopover` and every nested picker each registered their OWN raw
 * `document.addEventListener('mousedown', ...)`. Both fire on a single click
 * — DOM listeners don't know about each other — so clicking somewhere
 * outside EVERYTHING while a nested picker (e.g. the person "is any of"
 * multi-select) was open closed the picker AND the whole hosting
 * `ResponsivePopover` (discarding the in-progress filter draft — see
 * `FilterPopover.tsx`'s "QA-2" doc) in one gesture, even though the rep's
 * intent was just to dismiss the picker and keep editing. Centralizing "who
 * owns this click" into a stack makes only the topmost (most recently
 * opened) layer evaluate it — the outer popover defers entirely while
 * anything is open above it, exactly like `escape-stack.ts` does for Escape.
 *
 * ── Single-layer callers are unaffected ────────────────────────────────────
 * When only one layer is registered (the common case — no nested picker
 * open), it's also the top of the stack, so its own outside-click check runs
 * exactly as before. Nothing changes until a SECOND layer opens on top.
 *
 * ── Swallowing the dismissing click (Spencer report, 2026-08-13, round 2)
 * ────────────────────────────────────────────────────────────────────────
 * `ResponsivePopover` renders no backdrop — it's a lightweight popover, not
 * a modal (see its own doc comment) — so an outside click was a REAL click
 * on whatever was actually there underneath: dismissing the panel AND
 * activating that element in one gesture (e.g. a click that closed the
 * Filters popover also toggled a sidebar control it happened to land on,
 * visibly changing the page). Standard fix for any dismissible layer (same
 * technique Radix's `DismissableLayer`/`pointerDownOutside` and Headless
 * UI's popover use): when the topmost layer decides a `mousedown` is
 * outside and closes on it, that decision ALSO cancels the mousedown
 * (`preventDefault` + `stopPropagation`, so it never reaches the real
 * target) and arms a one-shot flag that cancels the immediately-following
 * `click` event too — `mousedown`→`click` are two separate events; cancelling
 * only the first leaves the second free to still fire the target's own
 * `onClick` normally. Net effect: the closing click is fully consumed,
 * never reaching whatever was underneath.
 *
 * ── Teardown must NOT race arming the swallow (bug, Spencer report
 * 2026-08-13, round 3 — the round-2 fix above was incomplete) ─────────────
 * The layer that just closed unregisters itself, and `onClose()`/
 * `setOpen(false)` — called SYNCHRONOUSLY from `top.onOutsideClick(target)`
 * below, i.e. from a RAW `document` listener outside React's own event
 * system — can flush its state update (and therefore run the effect
 * cleanup that unregisters the layer) SYNCHRONOUSLY, before
 * `onOutsideClick` even returns. `decisionInFlight` covers exactly that
 * window: it's set true BEFORE calling `onOutsideClick` and false right
 * after, so if the unregister's `teardownListenerIfEmpty` call lands
 * WHILE we're still inside `onOutsideClick`, it defers rather than
 * removing the very listeners this function needs to arm the swallow on
 * (`swallowTarget` is only set once `onOutsideClick` has returned and told
 * us `closed`). Missing this window was the round-2 bug: `swallowTarget`
 * was set AFTER `onOutsideClick` returned, by which point the listeners
 * could already be gone — so the very next real `click` was never
 * intercepted at all, and the underlying click-through kept happening.
 * `handleClick` re-checks teardown once it has consumed the flag, so the
 * stack still tears down (just one tick later) once nothing is left to
 * swallow.
 *
 * ── The armed flag self-expires if no click ever follows ─────────────────
 * A `mousedown` doesn't guarantee a matching `click`: if the pointer drags
 * off to an unrelated element before release, the browser fires `mouseup`
 * there but never synthesizes a `click` at all. Without an expiry, that
 * would leave `swallowNextClick` stuck `true` until the NEXT mousedown this
 * module sees resets it (see the top of `handleMouseDown`) — a real,
 * if narrow, window where some unrelated LATER click (e.g. one dispatched
 * programmatically, or via assistive tech, with no preceding mousedown of
 * its own) gets wrongly swallowed. A same-tick `click` (the overwhelmingly
 * common real case) is handled and clears the flag well before this timer
 * fires; the timer exists purely to bound the window for the drag-cancel
 * case above.
 *
 * ── Only the SAME-target click is swallowed ───────────────────────────────
 * The armed flag also records the mousedown's own `target`. A genuine
 * single click always fires `mousedown` and `click` on the same target
 * (that's how the browser decides a click happened at all) — so requiring a
 * match costs nothing for the real gesture this exists to swallow, while
 * refusing to eat a genuinely SEPARATE, later click that lands on a
 * different element (e.g. closing a popover with one click on the backdrop,
 * then deliberately clicking a DIFFERENT trigger to reopen something —
 * two distinct clicks, only the first of which this module ever saw a
 * mousedown for).
 *
 * Accepted trade-off (explicit product decision, not an oversight): clicking
 * a SIBLING toolbar trigger (e.g. "Columns" while "Filters" is open) used to
 * close Filters and open Columns in one click, via this same click-through.
 * That one-click switch is gone now too — dismissing and re-triggering
 * anything is uniformly two clicks. Swallowing is intentionally
 * click-target-agnostic (no allowlist of "safe" targets) — see the
 * conversation log for the decision.
 *
 * ── Why a callback, not a boolean (updated) ───────────────────────────────
 * Unlike Escape (the topmost layer unconditionally owns the keypress), an
 * outside click's effect still depends on WHERE it landed relative to THAT
 * layer's own panel/trigger — so the stack hands the topmost layer the raw
 * click target and lets it run its own inside/outside geometry check, it
 * just never lets a layer BENEATH the top evaluate the same click at all.
 * The handler now RETURNS whether it closed (`true`) so this module knows
 * whether to arm the click-swallow — a click that a layer decides is
 * "inside" (returns `false`) must reach its real target completely
 * normally (e.g. picking a menu item inside the layer itself).
 */

type OutsideClickHandler = (target: Node) => boolean;

interface OutsideClickLayer {
  id: number;
  onOutsideClick: OutsideClickHandler;
}

let stack: OutsideClickLayer[] = [];
let nextId = 0;
let listenerInstalled = false;
/** Armed by a closing `mousedown`; consumed by the very next SAME-target `click`. */
let swallowTarget: Node | null = null;
/** True for the duration of a `top.onOutsideClick(target)` call — see the
 * module doc's "Teardown must NOT race arming the swallow". */
let decisionInFlight = false;

function handleMouseDown(e: MouseEvent): void {
  // A prior mousedown's swallow that never got consumed (its matching click
  // never fired — e.g. the gesture ended as a drag, not a click) must not
  // leak into THIS unrelated click.
  swallowTarget = null;
  const target = e.target;
  if (!(target instanceof Node)) return;
  const top = stack[stack.length - 1];
  if (!top) return;
  decisionInFlight = true;
  const closed = top.onOutsideClick(target);
  decisionInFlight = false;
  if (closed) {
    e.preventDefault();
    e.stopPropagation();
    swallowTarget = target;
    // Self-expire — see the module doc's "The armed flag self-expires if no
    // click ever follows". A macrotask runs strictly after the synchronous
    // mousedown→mouseup→click sequence of a REAL click on the same gesture,
    // so this never races `handleClick` when a click does follow.
    setTimeout(() => {
      if (swallowTarget === target) {
        swallowTarget = null;
        teardownListenerIfEmpty();
      }
    }, 0);
  } else {
    // Nothing armed — if the callback's own bookkeeping happened to change
    // the stack without closing (not a real case today, but not this
    // function's business to assume), let a deferred teardown catch up.
    teardownListenerIfEmpty();
  }
}

function handleClick(e: MouseEvent): void {
  if (swallowTarget === null || e.target !== swallowTarget) return;
  swallowTarget = null;
  e.preventDefault();
  e.stopPropagation();
  // The stack may have gone empty WHILE this swallow was pending (see the
  // module doc's "Teardown must NOT race the pending swallow") — now that
  // it's consumed, finish the deferred teardown if nothing re-registered.
  teardownListenerIfEmpty();
}

function ensureListenerInstalled(): void {
  if (listenerInstalled) return;
  // Capture phase — same load-bearing reason as `useClickOutside.ts`'s "F-C"
  // doc comment: a nested handler's own bubble-phase mutation of the DOM
  // must not run before this check sees the click. `click` is captured too
  // (see the module doc's "swallowing" section) so the SEPARATE click event
  // that follows a cancelled mousedown never reaches its real target either.
  document.addEventListener('mousedown', handleMouseDown, true);
  document.addEventListener('click', handleClick, true);
  listenerInstalled = true;
}

function teardownListenerIfEmpty(): void {
  // Deferred while a swallow is pending OR a close decision is still being
  // made — see the module doc's "Teardown must NOT race arming the
  // swallow". `handleClick` (and `handleMouseDown`'s own post-decision
  // branches) re-call this once it's safe.
  if (stack.length > 0 || !listenerInstalled || swallowTarget !== null || decisionInFlight) return;
  document.removeEventListener('mousedown', handleMouseDown, true);
  document.removeEventListener('click', handleClick, true);
  listenerInstalled = false;
}

/**
 * Registers a layer that should own the next outside mousedown while it's
 * the topmost registered layer. `onOutsideClick` returns `true` iff it
 * closed on this click (arming the click-swallow above), `false` if the
 * click was inside and nothing happened. Returns an unregister function —
 * call it from the same effect's cleanup. Removal is by the layer's own id,
 * not a blind stack pop, so unregistering is safe regardless of push/pop
 * ordering (including StrictMode's double-invoke).
 */
export function pushOutsideClickLayer(onOutsideClick: OutsideClickHandler): () => void {
  const id = ++nextId;
  stack.push({ id, onOutsideClick });
  ensureListenerInstalled();
  return () => {
    stack = stack.filter(layer => layer.id !== id);
    teardownListenerIfEmpty();
  };
}
